import { useState, useEffect, useCallback } from 'react';
import { Settings2, Plus, Pencil, Trash2, Loader2, Shield } from 'lucide-react';
import { useAPIStorage } from '../../hooks/useAPIStorage';
import type { AbsenceType, AbsenceTypeColor, ManagerVisibility } from '../../types';
import { ABSENCE_TYPE_COLORS, MANAGER_VISIBILITY_LABELS, MANAGER_VISIBILITY_OPTIONS } from '../../types';
import { ABSENCE_COLOR_CLASSES } from '../../utils/absenceTypeColors';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface EditState {
  id?: string;
  name: string;
  shortName: string;
  color: AbsenceTypeColor;
  icon: string;
  countsAsVacation: boolean;
  countsAsSickDay: boolean;
  isPaid: boolean;
  allowHalfDay: boolean;
  excludeFrom13WeekAvg: boolean;
  skipInVacationBooking: boolean;
  requiresApproval: boolean;
  managerVisibility: ManagerVisibility;
  isSystem?: boolean;
}

const EMPTY_EDIT: EditState = {
  name: '',
  shortName: '',
  color: 'purple',
  icon: '',
  countsAsVacation: false,
  countsAsSickDay: false,
  isPaid: true,
  allowHalfDay: true,
  excludeFrom13WeekAvg: true,
  skipInVacationBooking: true,
  requiresApproval: false,
  managerVisibility: 'FULL',
};

export function AbsenceTypeSettings() {
  const { getAllAbsenceTypes, createAbsenceType, updateAbsenceType, deleteAbsenceType } = useAPIStorage();
  const [types, setTypes] = useState<AbsenceType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editState, setEditState] = useState<EditState>(EMPTY_EDIT);
  const [error, setError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setListError(null);
    try {
      const data = await getAllAbsenceTypes();
      setTypes(data);
    } catch (e) {
      // Mit Fehlermeldung rendern statt still auf leere Liste fallen.
      setListError(
        e instanceof Error ? e.message : 'Abwesenheitsarten konnten nicht geladen werden.',
      );
    } finally {
      setIsLoading(false);
    }
  }, [getAllAbsenceTypes]);

  useEffect(() => { void load(); }, [load]);

  const handleCreate = () => {
    setEditState(EMPTY_EDIT);
    setError(null);
    setEditOpen(true);
  };

  const handleEdit = (type: AbsenceType) => {
    setEditState({
      id: type.id,
      name: type.name,
      shortName: type.shortName ?? '',
      color: type.color as AbsenceTypeColor,
      icon: type.icon ?? '',
      countsAsVacation: type.countsAsVacation,
      countsAsSickDay: type.countsAsSickDay,
      isPaid: type.isPaid,
      allowHalfDay: type.allowHalfDay,
      excludeFrom13WeekAvg: type.excludeFrom13WeekAvg,
      skipInVacationBooking: type.skipInVacationBooking,
      requiresApproval: type.requiresApproval,
      managerVisibility: type.managerVisibility,
      isSystem: type.isSystem,
    });
    setError(null);
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!editState.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      if (editState.id) {
        await updateAbsenceType(editState.id, {
          name: editState.name.trim(),
          shortName: editState.shortName.trim() || null,
          color: editState.color,
          icon: editState.icon.trim() || null,
          requiresApproval: editState.requiresApproval,
          managerVisibility: editState.managerVisibility,
          ...(!editState.isSystem && {
            countsAsVacation: editState.countsAsVacation,
            countsAsSickDay: editState.countsAsSickDay,
            isPaid: editState.isPaid,
            allowHalfDay: editState.allowHalfDay,
            excludeFrom13WeekAvg: editState.excludeFrom13WeekAvg,
            skipInVacationBooking: editState.skipInVacationBooking,
          }),
        });
      } else {
        await createAbsenceType({
          name: editState.name.trim(),
          shortName: editState.shortName.trim() || null,
          color: editState.color,
          colorDark: null,
          icon: editState.icon.trim() || null,
          countsAsVacation: editState.countsAsVacation,
          countsAsSickDay: editState.countsAsSickDay,
          isPaid: editState.isPaid,
          allowHalfDay: editState.allowHalfDay,
          excludeFrom13WeekAvg: editState.excludeFrom13WeekAvg,
          skipInVacationBooking: editState.skipInVacationBooking,
          requiresApproval: false,
          managerVisibility: editState.managerVisibility,
          sortOrder: types.length + 1,
        });
      }
      setEditOpen(false);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (type: AbsenceType) => {
    if (deletingId) return; // Mehrfach-Klick blockieren
    if (!confirm(`"${type.name}" wirklich deaktivieren?`)) return;
    setListError(null);
    setDeletingId(type.id);
    try {
      await deleteAbsenceType(type.id);
      await load();
    } catch (e) {
      setListError(
        e instanceof Error ? e.message : 'Abwesenheitsart konnte nicht deaktiviert werden.',
      );
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6 flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Lade Abwesenheitsarten...
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Abwesenheitsarten
            </h3>
            <Button size="sm" onClick={handleCreate} className="flex items-center gap-1">
              <Plus className="w-4 h-4" />
              Neue Art
            </Button>
          </div>

          {listError && (
            <p
              role="alert"
              className="mb-4 text-sm text-destructive bg-destructive/10 border border-destructive/50 rounded-md px-3 py-2"
            >
              {listError}
            </p>
          )}

          <div className="space-y-2">
            {types.map((type) => {
              const colorClass = ABSENCE_COLOR_CLASSES[type.color];
              return (
                <div
                  key={type.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded ${colorClass?.bg ?? 'bg-gray-200'}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {type.icon && <span className="mr-1">{type.icon}</span>}
                          {type.name}
                        </span>
                        {type.isSystem && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            <Shield className="w-3 h-3 mr-0.5" />
                            System
                          </Badge>
                        )}
                        {!type.active && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                            Inaktiv
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1.5 mt-0.5">
                        {type.countsAsVacation && <Badge variant="info" className="text-[10px] px-1.5 py-0">Urlaub</Badge>}
                        {type.countsAsSickDay && <Badge variant="warning" className="text-[10px] px-1.5 py-0">Krankheit</Badge>}
                        {type.allowHalfDay && type.countsAsVacation && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Halbe Tage</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(type)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    {!type.isSystem && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(type)}
                        disabled={deletingId !== null}
                        className="text-red-500 hover:text-red-700"
                      >
                        {deletingId === type.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {types.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Keine Abwesenheitsarten konfiguriert.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bearbeiten/Erstellen-Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              {editState.id ? 'Abwesenheitsart bearbeiten' : 'Neue Abwesenheitsart'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <Label htmlFor="at-name">Name</Label>
              <Input
                id="at-name"
                value={editState.name}
                onChange={(e) => setEditState((s) => ({ ...s, name: e.target.value }))}
                placeholder="z.B. Bildungsurlaub"
                maxLength={60}
              />
            </div>

            {/* Kurzname + Icon */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="at-short">Kurzname (CSV)</Label>
                <Input
                  id="at-short"
                  value={editState.shortName}
                  onChange={(e) => setEditState((s) => ({ ...s, shortName: e.target.value }))}
                  placeholder="BU"
                  maxLength={10}
                />
              </div>
              <div>
                <Label htmlFor="at-icon">Icon (Emoji)</Label>
                <Input
                  id="at-icon"
                  value={editState.icon}
                  onChange={(e) => setEditState((s) => ({ ...s, icon: e.target.value }))}
                  placeholder="📚"
                  maxLength={10}
                />
              </div>
            </div>

            {/* Farbpalette */}
            <div>
              <Label>Farbe</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {ABSENCE_TYPE_COLORS.map((color) => {
                  const cls = ABSENCE_COLOR_CLASSES[color];
                  return (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setEditState((s) => ({ ...s, color }))}
                      className={`w-8 h-8 rounded-md ${cls?.bg ?? 'bg-gray-200'} transition-all ${
                        editState.color === color
                          ? 'ring-2 ring-offset-2 ring-gray-900 dark:ring-gray-100'
                          : 'hover:ring-1 hover:ring-gray-400'
                      }`}
                      title={color}
                    />
                  );
                })}
              </div>
            </div>

            {/* Sichtbarkeit fuer Abteilungsleitung (DSGVO Art. 9) — fuer alle Typen editierbar */}
            <div className="space-y-2 border-t pt-3">
              <Label htmlFor="manager-visibility" className="text-sm font-medium">
                Sichtbarkeit für Abteilungsleitung
              </Label>
              <Select
                value={editState.managerVisibility}
                onValueChange={(value) =>
                  setEditState((s) => ({ ...s, managerVisibility: value as ManagerVisibility }))
                }
              >
                <SelectTrigger id="manager-visibility">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MANAGER_VISIBILITY_OPTIONS.map((v) => (
                    <SelectItem key={v} value={v}>
                      {MANAGER_VISIBILITY_LABELS[v]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Steuert, was die Abteilungsleitung in Team-Ansichten sieht. &bdquo;Nur abwesend&ldquo;
                verbirgt den Typ aus Datenschutzgründen (DSGVO Art. 9, Gesundheitsdaten).
              </p>
            </div>

            {/* Verhaltens-Flags (nur für nicht-System-Typen) */}
            {!editState.isSystem && (
              <div className="space-y-3 border-t pt-3">
                <p className="text-sm font-medium text-muted-foreground">Verhalten</p>

                <Label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={editState.countsAsVacation}
                    onCheckedChange={(checked) =>
                      setEditState((s) => ({
                        ...s,
                        countsAsVacation: !!checked,
                        countsAsSickDay: checked ? false : s.countsAsSickDay,
                      }))
                    }
                  />
                  <span className="text-sm">Zählt als Urlaubstag (wird vom Urlaubsanspruch abgezogen)</span>
                </Label>

                <Label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={editState.countsAsSickDay}
                    onCheckedChange={(checked) =>
                      setEditState((s) => ({
                        ...s,
                        countsAsSickDay: !!checked,
                        countsAsVacation: checked ? false : s.countsAsVacation,
                      }))
                    }
                  />
                  <span className="text-sm">Zählt als Krankheitstag (EFZG-Berechnung)</span>
                </Label>

                {editState.countsAsVacation && (
                  <Label className="flex items-center gap-3 cursor-pointer ml-6">
                    <Checkbox
                      checked={editState.allowHalfDay}
                      onCheckedChange={(checked) =>
                        setEditState((s) => ({ ...s, allowHalfDay: !!checked }))
                      }
                    />
                    <span className="text-sm">Halbe Tage erlauben</span>
                  </Label>
                )}

                <Label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={editState.isPaid}
                    onCheckedChange={(checked) =>
                      setEditState((s) => ({ ...s, isPaid: !!checked }))
                    }
                  />
                  <span className="text-sm">Bezahlt (Stunden werden berechnet)</span>
                </Label>

                <Label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={editState.skipInVacationBooking}
                    onCheckedChange={(checked) =>
                      setEditState((s) => ({ ...s, skipInVacationBooking: !!checked }))
                    }
                  />
                  <span className="text-sm">Bei Urlaubsblock-Buchung überspringen</span>
                </Label>

                <Label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={editState.requiresApproval}
                    onCheckedChange={(checked) =>
                      setEditState((s) => ({ ...s, requiresApproval: !!checked }))
                    }
                  />
                  <span className="text-sm">Genehmigung durch HR/Manager erforderlich</span>
                </Label>
              </div>
            )}

            {editState.isSystem && (
              <div className="border-t pt-3 space-y-3">
                <Label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={editState.requiresApproval}
                    onCheckedChange={(checked) =>
                      setEditState((s) => ({ ...s, requiresApproval: !!checked }))
                    }
                  />
                  <span className="text-sm">Genehmigung durch HR/Manager erforderlich</span>
                </Label>
                <p className="text-xs text-muted-foreground">
                  <Shield className="w-3.5 h-3.5 inline mr-1" />
                  System-Typ: Nur Name, Kurzname, Farbe, Icon, Genehmigungspflicht und Sichtbarkeit können bearbeitet werden.
                </p>
              </div>
            )}

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
